import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const money = (n, c = "USD") => `${c} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// One-click: instantiate an estimate template into a draft estimate for `clientId`,
// resolving all merge fields against the real record. Reusable across the
// Create-Estimate screen and lead/contact pages.
export function UseTemplateButton({ clientId, label = "Use a Template", variant = "solid", disabled = false, className = "", testid = "use-template-btn", onCreated }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (open && templates === null) {
      api.get("/sales/estimate-templates", { params: { include_inactive: false } })
        .then((r) => setTemplates(r.data.items || []))
        .catch(() => setTemplates([]));
    }
  }, [open, templates]);

  const use = async (tpl) => {
    if (!clientId) { toast.error("Select a client or lead first"); return; }
    setBusyId(tpl.id);
    try {
      const { data } = await api.post("/sales/estimates/from-template", { template_id: tpl.id, client_id: clientId });
      const est = data.estimate;
      const warn = data.unresolved_fields?.length ? { description: `Review before sending — needs data: ${data.unresolved_fields.map((t) => `{{${t}}}`).join(", ")}` } : undefined;
      toast.success(`${est.estimate_number} created from "${tpl.name}"`, warn);
      setOpen(false);
      if (onCreated) onCreated(est); else navigate(`/sales/estimates?open=${est.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create estimate"); }
    finally { setBusyId(null); }
  };

  const styles = variant === "solid"
    ? "bg-[#171B2E] text-white hover:bg-[#20263f]"
    : "border border-slate-300 text-slate-700 hover:bg-slate-50";

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={disabled}
        data-testid={testid}
        className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${styles} ${className}`}>
        <Icons.LayoutTemplate className="h-4 w-4" />{label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="use-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Estimate from Template</DialogTitle>
            <DialogDescription>Pick a template — a draft estimate is created and merge fields are resolved against this record.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
            {templates === null ? (
              <div className="py-8 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">No active estimate templates. Create one in Templates → Estimate Templates.</p>
            ) : templates.map((t) => {
              const total = (t.line_items || []).reduce((s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0);
              return (
                <button key={t.id} onClick={() => use(t)} disabled={busyId != null}
                  data-testid={`use-template-item-${t.id}`}
                  className="w-full text-left border border-slate-200 rounded-xl p-3 hover:border-blue-300 hover:bg-blue-50/40 transition-colors flex items-center justify-between gap-3 disabled:opacity-60">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 flex items-center gap-2">{t.name}{t.is_default && <span className="text-[10px] font-semibold uppercase bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">Default</span>}</div>
                    {t.description && <div className="text-xs text-slate-500 truncate">{t.description}</div>}
                    <div className="text-xs text-slate-400 mt-0.5">{(t.line_items || []).length} item{(t.line_items || []).length === 1 ? "" : "s"} · {money(total, t.currency)}</div>
                  </div>
                  {busyId === t.id ? <Icons.Loader2 className="h-4 w-4 animate-spin text-blue-500 shrink-0" /> : <Icons.ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UseTemplateButton;
